<template>
  <AttrSelect
    label="当前显示"
    class="w-full mt-2.5"
    :disabled="isSlotFollowing"
    :value="widgetIdToDisplay"
    :options="widgets"
    @change="(val:string)=>onWidgetIdDisplayChange(val)"
  />
  <template v-if="shape.attrs.slotType == 'widget'">
    <AttrSwitch
      class="mt-2.5"
      label="可选无"
      :value="shape.attrs.includeEmpty"
      :disabled="!!attrDisabled || isSlotFollowing"
      @change="onChange"
    />

    <!-- 容器自定义位置 -->
    <SlotPosition v-if="showSlotPosition" :shape="shape" />
    <AttrSelect
      v-if="isSupportSlotFollowing"
      label="跟随亮屏 -> 样式1"
      class="w-full mt-2.5"
      :value="followingSlot ?? 'none'"
      :options="followingSlotOptions"
      @change="changeFollowingSlot"
    />

    <!-- 容器列表 -->

    <AttrGroup title="容器列表">
      <div
        class="px-5 py-[15px] flex flex-col gap-2.5 bg-[#FA683A]/[0.1] rounded-lg rounded-dash"
        :class="{ 'non-editable': isSlotFollowing }"
      >
        <div v-for="(item, index) in widgets" :key="(item.label ?? '') + index">
          <div
            class="flex items-center px-5 py-3 bg-[#FA683A]/[0.1] rounded-lg border border-[#E66138]/[0.3] hover:bg-[#FA683A]/[0.2]"
            @click="() => onWidgetIdDisplayChange(item.value)"
          >
            <div class="flex-1 text-[#e66138]">
              {{ item.label }}
            </div>
            <div
              class="w-5 h-5 p-[2px] bg-[#FA683A]/[0.1] border border-[#E66138] rounded-xl"
            >
              <div
                class="w-full h-full rounded-xl"
                :class="{
                  'bg-[#E66138]': widgetIdToDisplay === item.value
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </AttrGroup>

    <!-- 边框图 容器内多个组 或 只有一个组但可选无时展示-->
    <SlotWidgetEditbox
      v-if="
        shape.children?.length > 1 ||
        (shape.children?.length === 1 && shape.attrs.includeEmpty)
      "
      :disabled="isSlotFollowing"
      class="mt-2"
      :shape="shape"
    />
    <!-- 预览图 -->
    <SlotWidgetPreview :shape="shape" />
  </template>
</template>
<script lang="ts" setup>
import { Shape } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useImagesStore } from '@/store/use-images-store';
import { computed, PropType } from 'vue';
import SlotPosition from './SlotPosition.vue';
import { GlobalConfig } from '@/services/gloabl.config';
import { canUse } from '@/utils/can-use.utils';
import { useSlotFollow } from '@/hooks/use-slot-follow';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { ElectronApi } from '@/services/electron.api';

const imageStore = useImagesStore();
const editorStore = useEditorStore();
const watchfaceStore = useWatchfaceStore();
const attrDisabled = computed(() => editorStore.attrDisabled);
const { findFollowingSlotOptions, setFollowingId, followParentAttrs } =
  useSlotFollow();
const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  },
  slotType: {
    type: String,
    default: 'widget'
  },
  followingSlot: {
    type: String,
    default: undefined
  },
  isSupportSlotFollowing: {
    type: Boolean,
    default: false
  },
  isSlotFollowing: {
    type: Boolean,
    default: false
  }
});

const widgets = computed(() => {
  const res = props.shape.children.map((item) => {
    return {
      label: item.attrs.name,
      value: item.id
    };
  });
  res.reverse();
  if (props.shape.attrs.includeEmpty) {
    if (canUse('none-widget-last').valid)
      res.push({
        value: 'none',
        label: '无'
      });
    else
      res.unshift({
        value: 'none',
        label: '无'
      });
  }
  return res;
});

const widgetIdToDisplay = computed(() => {
  const id = props.shape.attrs.widgetIdToDisplay;
  if (widgets.value.some((item) => item.value == id)) {
    return id;
  } else {
    return widgets.value.find((item) => item.value != 'none')?.value;
  }
});

const onWidgetIdDisplayChange = (val: string) => {
  const curShape = props.shape;
  curShape.setAttrs({ widgetIdToDisplay: val });
  if (curShape.attrs.followingGroupName !== undefined)
    followParentAttrs(curShape, 'widgetIdToDisplay', val);
};

const onChange = (checked: boolean) => {
  if (checked) {
    // 创建一个空的image
    const basename = imageStore.addEmptyImage();
    props.shape.setAttrs({
      includeEmpty: !!checked,
      emptyImageSrc: basename
    });
  } else {
    const widgetIdToDisplay = props.shape.attrs.widgetIdToDisplay;
    props.shape.setAttrs({
      includeEmpty: !!checked,
      emptyImageSrc: '',
      widgetIdToDisplay: widgetIdToDisplay == 'none' ? '' : widgetIdToDisplay
    });
  }
  if (props.shape.attrs.followingGroupName !== undefined)
    followParentAttrs(props.shape, 'includeEmpty', checked);
};

const followingSlotOptions = computed(() => findFollowingSlotOptions());

// 外部版本的相册表盘不显示自定义位置功能
const showSlotPosition = computed(() => {
  if (!GlobalConfig.slotPosiontEnabled) return false;
  if (ElectronApi.isNormal() && watchfaceStore.watchfaceType === 'photoAlbum') return false;
  return true;
});

const changeFollowingSlot = (val: string) => {
  setFollowingId(val, props.shape);
};
</script>
<style scoped lang="less">
.rounded-dash {
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23FA683A88' stroke-width='1' stroke-dasharray='12%2c 12' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e");
  background-size: 100% 100%;
  background-repeat: no-repeat;
}
.non-editable {
  pointer-events: none;
  opacity: 0.6;
}
</style>
